// Static sub-app manifest. In production this would be served by the
// orchestration plane (per doc 04 / D11.A integration mode).

import type { AppInputContract, ManifestPermissions, SchemaVersion } from "./manifest-schema";

export type IntegrationMode = "iframe" | "module-federation" | "inline";

// Chrome mode contract — mirrors PWA manifest `display`. `auth` is a hard
// no-iframe (Auth0 rule: embedded login breaks SSO + FedCM).
export type ChromeMode = "default" | "fullscreen" | "embed" | "auth";

// App lifecycle status. `active` = no badge (silence-as-signal per research).
export type AppStatus = "active" | "beta" | "internal" | "maintenance" | "deprecated";

// ── Apps-tab taxonomy ────────────────────────────────────────────────────────────────────────
// The outcome an app serves (the axis the Apps tab groups by) and its delivery maturity. Both are
// OPTIONAL and neither is defaulted: an app that declares no category renders in the uncategorised
// band rather than being placed in a column it was never authored into, and an app that declares no
// lifecycle shows no lifecycle chip. Enums mirror APP_CATEGORIES / APP_LIFECYCLES in
// manifest-schema.ts, which the validator checks membership against.
export type AppCategory = "platform" | "margin-improvement" | "complexity-reduction" | "growth" | "sustainability";
export type AppLifecycle = "live" | "validating" | "concept";

// The tenant scope levels an app can be MOUNTED at (V2 app-platform). The scope tree is
// firm › workspace › client › engagement › project › app; an app mounts at one of the four
// user-facing tenant levels (firm = system, app = the app's own data anchor — neither is a mount).
export type ScopeLevel = "workspace" | "client" | "engagement" | "project";

/** Shell-owned surfaces participate in app routing and the open-app tray without
 * becoming launcher-managed product apps or persisted AppInstance placements. */
export const ZARVIS_APP_ID = "zarvis" as const;
export const ZARVIS_DESCRIPTION =
  "Ask about the current scope. Zarvis searches permitted workspace and company sources and keeps evidence with each answer.";

export interface SubAppManifest {
  id: string;
  name: string;
  description: string;
  // Route prefix inside the AppShell (e.g., "/apps/cost-modeling")
  route: string;
  // Dev origin where the sub-app is served — must match Vite dev server.
  origin: string;
  // Path inside the sub-app to load first.
  entryPath: string;
  // Locked to "iframe" for this prototype per D11.A.
  mode: IntegrationMode;
  // Default = full chrome; fullscreen = no sidebar; embed = no shell chrome.
  chrome?: ChromeMode;
  status?: AppStatus;
  // The outcome this app serves. Omitted ⇒ uncategorised (renders in its own band, never defaulted
  // into an outcome column). `platform` is the engagement-running layer, not an outcome.
  category?: AppCategory;
  // Delivery maturity, orthogonal to `status`: `status` gates runtime behaviour, `lifecycle` is what
  // the Apps tab filters on. Every live entry is status "active", so status cannot carry this.
  lifecycle?: AppLifecycle;
  icon: string;
  // Declared capabilities the sub-app needs.
  requiredCapabilities: string[];
  // Per 13_Manifest:46-54 + 15_UAM:317-318: if false, app mounts at /g/:rg/...
  // without /e/:eng/ (e.g. settings, DS Explorer). Default true.
  // DEPRECATED by `mountLevels` (V2); do NOT use it to gate mounting — mounting is governed by
  // `mountLevels`/`appCanMountAt` only. New apps set `mountLevels`. (A previously-referenced
  // `showEngagementChip` consumer never existed — comment corrected, ENGSCOPE-01 9.5.)
  engagementRequired?: boolean;
  // 9.7 (doc 15 §7:527-529): opt-in engagement-context enforcement. When true, the FE attaches
  // the per-engagement token (X-Engagement-Token) on this app's engagement-addressed calls and
  // the app's BE routes adopt require_engagement_context. Default absent = OFF (spec). v1: NO
  // app opts in — the FE token acquisition/refresh lifecycle is a named CFR; the BE dependency +
  // adversarial suite exist (workspace_backend/app/authz/engagement_gateway.py).
  gateway?: { enforceEngagementContext?: boolean };
  // The scope levels this app may be MOUNTED at (V2). Omitted ⇒ ["project"] (back-compat: every
  // pre-V2 app is project-mounted). An app mountable anywhere declares all four levels.
  mountLevels?: ScopeLevel[];
  // App Launcher data-readiness probe (knowledge/09 L2): a walled endpoint path fetched at the
  // current node (reader-scoped); a NON-EMPTY array ⇒ "data ready" (●), empty ⇒ "needs import"
  // (○ → the level's Data tab). Omitted ⇒ the generic fallback (canonical sources at the node).
  dataReadiness?: {
    probe: string;
    // Producer apps set requiredForLaunch=false when empty data is a valid first-use state.
    requiredForLaunch?: boolean;
    // Clickflows may ship deterministic in-app fixtures and therefore remain launchable when the
    // live-data probe is empty. This does not claim the backend is ready; launcher copy identifies
    // the experience as a demo and the app itself keeps its prototype boundary visible.
    launchWithDemoData?: boolean;
  };
  // ── cat-25 manifest v1.2 NEAR-TERM CORE (VW-BATCH-20260704-MANIF-01) ──
  // permissions ships v1.1-FLAT; only clientAccessTier is wired (the rest of the doc12 permissions
  // block is CFR-MANIF01). D-ALMP-02: the 3-tier identity:/project:/data: restructure is the
  // superseding v1.2 target owned by cat-32 ALM — NOT built here. clientAccessTier is
  // DECLARATION-ONLY (no cat-8 runtime gate reads it — CFR).
  permissions?: ManifestPermissions;
  // Manifest schema version. v1.1-flat this batch (see manifest-schema.SCHEMA_VERSION). Omitted ⇒
  // treated as legacy/pre-versioned by the validator.
  schemaVersion?: SchemaVersion;
  // Row 40.9 (CAND-T6-CAT40): declares whether this app's artifacts can be exported as a client
  // deliverable via POST /egress/deliverables/render, and which render formats it supports. Omitted ⇒
  // export is not offered for this app's artifacts. `exportFormats` mirrors
  // `deliverable_renderer.SUPPORTED_RENDER_FORMATS` on the backend (docx/pptx/xlsx/pdf/html) — kept as
  // a plain string array here (not re-exported types) since this is a static, hand-maintained registry
  // with no build-time link to the backend module.
  deliverable?: { exportSupported?: boolean; exportFormats?: string[] };
  // MB5 §4: the typed governed-input consumption contract — the SINGLE source of an app's readiness
  // requirements + its adapter binding + declared result types. Omitted ⇒ the app declares no governed
  // inputs (viewer/legacy) and readiness falls back to the generic probe. Validated by validateManifest.
  inputContract?: AppInputContract;
}

// Re-export the v1.2 schema constants + validator so consumers import them from @perlab/registry.
export * from "./manifest-schema";
export { validateManifest, validateRegistry, readLegacyManifest } from "./validate";
export type { ManifestError, ManifestValidation } from "./validate";

// Only apps with a REAL mount are registered. Genuinely-absent iframe demo apps (dashboard, collab-doc,
// iframe-app) were manifest-only — no guest code, no live origin — so they rendered an honest placeholder;
// removed 2026-06-29. The iframe platform (mount lane + bridge in scout-iframe-mount/) stays; re-add an
// entry here only when a real guest app actually serves its origin.
//
// MB5 M5 (split-brain convergence): cost-modeling is a REAL inline app (CostModelingView, mounted via
// INLINE_SUBAPPS) — it was mounting but was absent from THIS canonical registry, so getSubApp / the
// launcher / appCanMountAt never saw it. It is now registered here as the single source of truth; its
// local sub-apps/cost-modeling/manifest.ts re-exports this entry (no id alias needed — id is identical).
export const SUBAPPS: SubAppManifest[] = [
  {
    id: "library",
    name: "Library",
    description: "Workspace-level artifact/knowledge library — promoted artifacts and pending candidates.",
    route: "/apps/library",
    // mode "inline" = native in-shell mount (no separate origin); origin unused.
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    // The engagement-running layer, not an outcome: a workspace-level artifact shelf every app
    // reads from, which is what `platform` denotes (same band as Deliverable Studio).
    category: "platform",
    // Platform-provided, not a demo surface: real router (mounted with the platform routers) and a
    // real view that ships with the shell. No fixture lane, so nothing gates it behind validation.
    lifecycle: "live",
    icon: "file-text",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    // F4 fix: `engagementRequired` dropped — it contradicted the workspace mount (the field is
    // deprecated chip-cosmetics anyway; mounting is gated by mountLevels alone).
    // Workspace-level placement: with the sidebar SECTIONS region removed (nav-restructure,
    // knowledge/07), Library surfaces via the APPS region at workspace scope.
    mountLevels: ["workspace"],
    // cat-25 25.39 bulk-migration default (doc12:956, v3 lock Q14): most-restrictive, safe.
    // DECLARATION-ONLY — no runtime gate reads it yet (CFR → cat-8).
    permissions: { clientAccessTier: "kearney-internal-only" },
    // v1.1-flat manifest (25.26; D-ALMP-02: v1.2=3-tier is cat-32).
    schemaVersion: "1.1",
    deliverable: { exportSupported: true, exportFormats: ["docx", "pptx", "xlsx", "pdf", "html"] },
  },
  {
    id: "formulatr",
    name: "FormulatR",
    description: "CPG recipe/formulation cost-rationalization — surfaced savings opportunities, provenance-first (synthetic demo).",
    route: "/apps/formulatr",
    // mode "inline" = native in-shell mount (no separate origin); origin unused.
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    category: "complexity-reduction",
    lifecycle: "concept",
    icon: "flask-conical",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    // engagement-or-deeper: cost rationalization runs on an engagement's portfolio.
    mountLevels: ["engagement", "project"],
    // Launcher readiness (knowledge/09 L2): FormulatR is ready where raw materials exist at scope.
    dataReadiness: { probe: "/formulatr/raw-materials" },
    // cat-25 25.39 bulk-migration default (doc12:956, v3 lock Q14). DECLARATION-ONLY (CFR → cat-8).
    permissions: { clientAccessTier: "kearney-internal-only" },
    // v1.1-flat manifest (25.26).
    schemaVersion: "1.1",
    deliverable: { exportSupported: true, exportFormats: ["docx", "pptx", "xlsx", "pdf", "html"] },
  },
  {
    id: "ideas-generator",
    name: "Ideas RedTeam",
    description:
      "Design-to-Value cost-down engine — idea import + deterministic BOM discovery over the data layer; the 5-agent analysis pipeline lands in later chunks (synthetic demo).",
    route: "/apps/ideas-generator",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    // Its own description is the evidence: a Design-to-Value cost-down engine taking cost out of
    // what already exists, which is exactly what `margin-improvement` denotes.
    category: "margin-improvement",
    // BOM discovery is real and wired to the data layer; the 5-agent analysis pipeline is not. Past
    // concept on the strength of the wired half, short of live until that pipeline lands.
    lifecycle: "validating",
    icon: "lightbulb",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    // engagement-or-deeper: cost-down runs on an engagement's/project's portfolio (doc 18:10).
    mountLevels: ["engagement", "project"],
    // Launcher readiness: DtV is ready where seeds exist at scope (probe = the seeds list).
    dataReadiness: { probe: "/ideas-generator/seeds" },
    permissions: { clientAccessTier: "kearney-internal-only" },
    schemaVersion: "1.1",
  },
  {
    id: "deliverable-studio",
    name: "Deliverable Studio",
    description:
      "Governed deliverable authoring — methodology-templated modules, immutable version chains, evidence-bound claims (synthetic demo). Authoring/runs/Build Report land in later chunks.",
    route: "/apps/deliverable-studio",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    category: "platform",
    lifecycle: "concept",
    icon: "layout-panel-left",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    // Deliverables belong to an engagement (occasionally a project) — doc 25 D9.
    mountLevels: ["engagement", "project"],
    // Launcher readiness: the Studio is ready where artifacts exist at scope.
    dataReadiness: { probe: "/deliverable-studio/artifacts" },
    permissions: { clientAccessTier: "kearney-internal-only" },
    schemaVersion: "1.1",
    deliverable: { exportSupported: true, exportFormats: ["pptx", "pdf", "html"] },
  },
  {
    id: "brainstorming",
    name: "DtV Engine",
    description:
      "Turn a live or recorded conversation into a fully attributed transcript, evidence-linked ideas, and governed downstream action.",
    route: "/apps/brainstorming",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    category: "margin-improvement",
    lifecycle: "live",
    icon: "mic",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    // A working session may start at workspace, client, engagement, or project scope (producer → DtV Engine).
    mountLevels: ["workspace", "client", "engagement", "project"],
    // Launcher readiness: Brainstorming is ready wherever sessions can be created/read at scope.
    dataReadiness: { probe: "/brainstorming/sessions", requiredForLaunch: false },
    permissions: { clientAccessTier: "kearney-internal-only" },
    schemaVersion: "1.1",
  },
  {
    id: "sentiment-workbench",
    name: "Semantic Workbench",
    description:
      "Prepare review data, shape a taxonomy, and analyse sentiment with portfolio, topic, and review insights and exportable results.",
    route: "/apps/sentiment-workbench",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    category: "growth",
    lifecycle: "validating",
    icon: "message-square",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    mountLevels: ["workspace", "client", "engagement", "project"],
    // Upload is the first step; an empty private history must not prevent launch.
    dataReadiness: { probe: "/sentiment-workbench/history", requiredForLaunch: false },
    permissions: { clientAccessTier: "kearney-internal-only" },
    schemaVersion: "1.1",
  },
  {
    id: "studio",
    name: "Studio",
    description:
      "Turn a project idea into a polished, interactive workflow. Add approved sample data, review private previews, and refine immutable versions in Sandbox Studio.",
    route: "/apps/studio",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    category: "platform",
    lifecycle: "live",
    icon: "sparkles",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    mountLevels: ["project"],
    // Studio produces workflows; an empty project is a valid first-use state.
    dataReadiness: { probe: "/studio/applications", requiredForLaunch: false },
    permissions: { clientAccessTier: "kearney-internal-only" },
    schemaVersion: "1.1",
  },
  {
    id: "design-intelligence",
    name: "Design Intelligence",
    description:
      "One product, three analysis lenses — Documented (2D drawings), Designed (3D CAD), Observed (teardown images) — converging on canonical parts and governed evidence. The parts contract and the evidence lineage are real; the data is seeded and the analysis engine is not yet wired.",
    route: "/apps/design-intelligence",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    category: "margin-improvement",
    lifecycle: "validating",
    icon: "scan-search",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    // Analysis runs on an engagement's/project's product portfolio (doc 49 IA; F-1 locked).
    mountLevels: ["engagement", "project"],
    // MB6 M5: governed input contract — DI consumes canonical PARTS (required) + CONFIGURATIONS (optional)
    // at the reader scope (its own fixtures carry parts/configuration; part.py/configuration.py are the
    // canonical wall-scoped entities). Readiness is now governed manifest-readiness (parts at scope) via
    // appReadinessProbe — NOT the fixtures, NOT a generic probe. Adapter di.v1 validates parts → a
    // deterministic launch payload → a governed "design_intelligence" result (no AI engine yet: the
    // boundary + service contract are real, the analysis output is deferred — no fabrication).
    inputContract: {
      capability_version: "v1",
      required_inputs: [
        { name: "parts", ref_type: "part", required: true, min_count: 1, max_count: 9999 },
      ],
      optional_inputs: [
        { name: "configurations", ref_type: "configuration", required: false, min_count: 0, max_count: 9999 },
      ],
      allowed_ref_types: ["part", "configuration"],
      version_policy: "current_only",
      scope_policy: "reader_scope",
      readiness_policy: "all_required",
      result_types: ["design_intelligence"],
      adapter_id: "di.v1",
    },
    permissions: { clientAccessTier: "kearney-internal-only" },
    schemaVersion: "1.1",
  },
  {
    id: "cost-modeling",
    name: "Cost Modeling",
    description:
      "Should-cost rollup over the scope's governed BOM — deterministic bottom-up cost modeling wired to bom_cost_rollup (COSTMODEL-01).",
    route: "/apps/cost-modeling",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    category: "margin-improvement",
    lifecycle: "validating",
    icon: "package",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    // project-scoped: a cost rollup runs over a project's BOM.
    mountLevels: ["project"],
    // MB6 M2: cost-modeling is the sole evidence-backed governed adoption. Its live reads are governed BOM
    // submodels (GET /submodels?kind=bom + /results/cost-rollup/{id}; CostModelingView.tsx:68,84,117) → a
    // cost rollup. Readiness is now manifest-driven governed readiness (appReadinessProbe posts
    // /app-consumption/manifest-readiness from this inputContract), replacing the generic /sources probe —
    // no more "some data exists" coarse signal. One BOM submodel at scope = ready.
    inputContract: {
      capability_version: "v1",
      required_inputs: [
        { name: "bom", ref_type: "submodel", required: true, min_count: 1, max_count: 9999 },
      ],
      optional_inputs: [],
      allowed_ref_types: ["submodel"],
      version_policy: "current_only",
      scope_policy: "reader_scope",
      readiness_policy: "all_required",
      result_types: ["cost"],
      adapter_id: "cost-modeling.v1",
    },
    permissions: { clientAccessTier: "kearney-internal-only" },
    schemaVersion: "1.1",
  },
];

/**
 * Native shell surfaces that use the same mount contract as apps but are opened
 * from shell chrome instead of the App Launcher. Keeping these outside SUBAPPS
 * prevents Zarvis from being offered as a mountable product or written to the
 * persisted app-placement registry.
 */
export const SHELL_SURFACES: SubAppManifest[] = [
  {
    id: ZARVIS_APP_ID,
    name: "Zarvis",
    description: ZARVIS_DESCRIPTION,
    route: "/apps/zarvis",
    origin: "",
    entryPath: "/",
    mode: "inline",
    chrome: "default",
    status: "active",
    // Personas, voice, conversation history, chart blocks and a tested image route are all wired,
    // with backend model-routing and retrieval tests behind them.
    lifecycle: "live",
    icon: "sparkles",
    requiredCapabilities: ["theme", "auth", "breadcrumb", "title"],
    mountLevels: ["workspace", "client", "engagement", "project"],
    permissions: { clientAccessTier: "client-collab" },
    schemaVersion: "1.1",
  },
];

const ROUTABLE_SURFACES = [...SHELL_SURFACES, ...SUBAPPS];

const LEGACY_SUBAPP_IDS: Readonly<Record<string, string>> = {
  // Temporary compatibility for saved links created before the product rename.
  roundtable: "brainstorming",
  sentiment_workbench: "sentiment-workbench",
};

const LEGACY_SUBAPP_ROUTES: Readonly<Record<string, string>> = {
  "/apps/roundtable": "/apps/brainstorming",
  "/apps/sentiment_workbench": "/apps/sentiment-workbench",
};

export function canonicalSubAppId(id: string): string {
  return LEGACY_SUBAPP_IDS[id] ?? id;
}

export function getSubApp(id: string): SubAppManifest | undefined {
  const canonicalId = canonicalSubAppId(id);
  return ROUTABLE_SURFACES.find((s) => s.id === canonicalId);
}

export function getSubAppByRoute(pathname: string): SubAppManifest | undefined {
  const direct = ROUTABLE_SURFACES.find(
    (s) => pathname === s.route || pathname.startsWith(`${s.route}/`),
  );
  if (direct) return direct;
  for (const [legacyRoute, canonicalRoute] of Object.entries(LEGACY_SUBAPP_ROUTES)) {
    if (pathname === legacyRoute || pathname.startsWith(`${legacyRoute}/`)) {
      return ROUTABLE_SURFACES.find((s) => s.route === canonicalRoute);
    }
  }
  return undefined;
}

export function isShellSurface(id: string): boolean {
  const canonicalId = canonicalSubAppId(id);
  return SHELL_SURFACES.some((surface) => surface.id === canonicalId);
}

/** The scope levels an app may mount at — `mountLevels` if declared, else ["project"] (back-compat). */
export function appMountLevels(app: SubAppManifest): ScopeLevel[] {
  return app.mountLevels && app.mountLevels.length > 0 ? app.mountLevels : ["project"];
}

/** True if `app` may be mounted at `level`. */
export function appCanMountAt(app: SubAppManifest, level: ScopeLevel): boolean {
  return appMountLevels(app).includes(level);
}

// ── Apps-tab category presentation ───────────────────────────────────────────────────────────
// Label + one-line caption per category. The captions say what the outcome MEANS; they are fixed
// editorial copy about the category itself, never a claim about the apps sitting in it, so a column
// carrying no app still reads truthfully.
export interface AppCategoryMeta {
  id: AppCategory;
  label: string;
  caption: string;
}

/** `platform` is deliberately absent: it is the engagement-running layer, not an outcome, and the
 *  Apps tab renders it as its own band rather than as one of the outcome columns. */
export const APP_OUTCOME_CATEGORIES: readonly AppCategoryMeta[] = [
  { id: "margin-improvement", label: "Margin improvement", caption: "Cost down on what already exists." },
  { id: "complexity-reduction", label: "Complexity reduction", caption: "Fewer SKUs, BOMs, recipes and packs." },
  { id: "growth", label: "Growth", caption: "New revenue, from outside evidence and from generation." },
  { id: "sustainability", label: "Sustainability", caption: "Footprint, circularity and lifecycle." },
];

export const APP_PLATFORM_CATEGORY: AppCategoryMeta = {
  id: "platform",
  label: "Platform",
  caption: "The platform itself. Runs the engagement rather than analysing the product.",
};

/** Human label for a lifecycle value. An app with no declared lifecycle has NO label — the caller
 *  renders nothing rather than inventing a maturity the manifest never claimed. */
export const APP_LIFECYCLE_LABELS: Readonly<Record<AppLifecycle, string>> = {
  live: "Live",
  validating: "Validating",
  concept: "Concept",
};
